export type SharedUiComponentCategory =
  | 'Navigation'
  | 'Data Display'
  | 'Form Controls'
  | 'Feedback'
  | 'Layout';

export interface SharedUiComponentDoc {
  id: string;
  name: string;
  selector: string;
  category: SharedUiComponentCategory;
  functionality: string;
  designNeeds: string;
  interactionNotes: string;
  usageGuidelines: string;
  usageStatus: 'High' | 'Medium';
}

export const SHARED_UI_COMPONENTS: SharedUiComponentDoc[] = [
  {
    id: 'ui-sidebar',
    name: 'Sidebar',
    selector: '<ui-sidebar>',
    category: 'Navigation',
    functionality: 'Primary shell navigation, active route highlighting, user identity footer and collapse handling.',
    designNeeds:
      'Steel-blue background, white iconography, gold active treatment, compact icon-only state and stable icon sizing.',
    interactionNotes:
      'Supports collapse/expand and route activation with clear visual selection.',
    usageGuidelines: 'Use once in shell layout. Keep labels short and route paths stable.',
    usageStatus: 'High'
  },
  {
    id: 'ui-topbar',
    name: 'Topbar',
    selector: '<ui-topbar>',
    category: 'Navigation',
    functionality: 'Displays breadcrumbs, quick actions and current-user area for page-level context.',
    designNeeds:
      'White surface with subtle border, Novecento hierarchy for navigation text and clear spacing between controls.',
    interactionNotes: 'Breadcrumb updates with route changes and provides action slots.',
    usageGuidelines: 'Use in shell above router outlet for every authenticated screen.',
    usageStatus: 'High'
  },
  {
    id: 'ui-page-header',
    name: 'Page Header',
    selector: '<ui-page-header>',
    category: 'Layout',
    functionality: 'Provides a dedicated action bar section for page-level commands.',
    designNeeds:
      'Clean spacing, actions aligned right, clear separation from content section below.',
    interactionNotes: 'Groups controls for accessibility using an actions aria label.',
    usageGuidelines: 'Use at top of list/detail pages where actions like create or export are present.',
    usageStatus: 'High'
  },
  {
    id: 'ui-page-tabs-header',
    name: 'Page Tabs Header',
    selector: '<ui-page-tabs-header>',
    category: 'Layout',
    functionality: 'Sticky page intro wrapper for title, description and optional tab navigation.',
    designNeeds:
      'Stable sticky offset below topbar, clean section border, and balanced headline-to-tab spacing.',
    interactionNotes: 'Supports route-local tab switching while keeping title context visible during long-form scrolling.',
    usageGuidelines: 'Use for create/edit/detail pages that need persistent title context with optional tabs.',
    usageStatus: 'High'
  },
  {
    id: 'ui-form-section',
    name: 'Form Section',
    selector: '<ui-form-section>',
    category: 'Layout',
    functionality: 'Reusable card section wrapper for grouped form fields with title and description.',
    designNeeds:
      'Consistent card spacing, restrained section typography and enough inner rhythm for dense admin forms.',
    interactionNotes: 'Uses content projection so any shared form wrapper can be slotted into the same section shell.',
    usageGuidelines: 'Use for vertical form flows instead of repeating ad-hoc card and header markup.',
    usageStatus: 'High'
  },
  {
    id: 'ui-form-action-footer',
    name: 'Form Action Footer',
    selector: '<ui-form-action-footer>',
    category: 'Layout',
    functionality: 'Standard footer row for Back, Save and Next actions across multi-step or single-step forms.',
    designNeeds:
      'Clear top divider, left/right action separation and responsive stacking without losing action hierarchy.',
    interactionNotes: 'Emits back/save/next events and supports loading and conditional action visibility.',
    usageGuidelines: 'Use at the bottom of create/edit forms to keep action placement consistent across modules.',
    usageStatus: 'High'
  },
  {
    id: 'ui-card',
    name: 'Card',
    selector: '<ui-card>',
    category: 'Layout',
    functionality: 'Standard white surface wrapper for grouped information blocks.',
    designNeeds:
      'Card border token, medium radius, generous internal spacing and restrained title typography.',
    interactionNotes: 'Content projection allows composing any section safely.',
    usageGuidelines: 'Use for all panel blocks instead of ad-hoc containers.',
    usageStatus: 'High'
  },
  {
    id: 'ui-stat-card',
    name: 'Stat Card',
    selector: '<ui-stat-card>',
    category: 'Data Display',
    functionality: 'Highlights headline KPI values with optional delta context.',
    designNeeds:
      'Gold top accent, strong value typography and compact secondary label rhythm.',
    interactionNotes: 'Supports concise value scanning in dashboard-like rows.',
    usageGuidelines: 'Use in stat rows for top-level KPI snapshots.',
    usageStatus: 'High'
  },
  {
    id: 'ui-table',
    name: 'Table',
    selector: '<ui-table>',
    category: 'Data Display',
    functionality:
      'Shared grid with search, sort, filter, pagination, row selection, actions and detail panel support.',
    designNeeds:
      'Fixed layout columns, blue header with gold divider, dense rows and consistent status badge rendering.',
    interactionNotes: 'Handles row click, action click, bulk action and detail drawer behavior.',
    usageGuidelines: 'Use for all list views. Configure columns, filters and actions via typed inputs.',
    usageStatus: 'High'
  },
  {
    id: 'ui-chart',
    name: 'Chart',
    selector: '<ui-chart>',
    category: 'Data Display',
    functionality: 'Reusable chart wrapper for trend and distribution visuals in reports and dashboard areas.',
    designNeeds:
      'Readable axis contrast, brand palette highlights, and sufficient plot spacing for smaller screens.',
    interactionNotes: 'Designed to accept chart data configs and remain responsive.',
    usageGuidelines: 'Use when trend comparison is better than tabular scanning.',
    usageStatus: 'Medium'
  },
  {
    id: 'ui-pipeline-strip',
    name: 'Pipeline Strip',
    selector: '<ui-pipeline-strip>',
    category: 'Data Display',
    functionality: 'Displays stage-based progression for workflow pipelines.',
    designNeeds:
      'Horizontal stage rhythm with active gold emphasis and clear stage contrast.',
    interactionNotes: 'Best used for at-a-glance lifecycle progress visibility.',
    usageGuidelines: 'Use on workflow pages where stage progression is central.',
    usageStatus: 'Medium'
  },
  {
    id: 'ui-filter-bar',
    name: 'Filter Bar',
    selector: '<ui-filter-bar>',
    category: 'Form Controls',
    functionality: 'Provides reusable filter/search controls to refine large datasets.',
    designNeeds:
      'Compact spacing, clean alignment with table tools and clear active-filter indication.',
    interactionNotes: 'Supports rapid filter changes with minimal layout shift.',
    usageGuidelines: 'Use above dense lists and analytic grids.',
    usageStatus: 'Medium'
  },
  {
    id: 'ui-form-field',
    name: 'Form Field',
    selector: '<ui-form-field>',
    category: 'Form Controls',
    functionality: 'Standardized text input wrapper with labels, value binding and error display.',
    designNeeds:
      'Consistent label styling, border tokens, blue focus state and readable error color contrast.',
    interactionNotes: 'Emits value changes and supports inline validation messaging.',
    usageGuidelines: 'Use for all text-like inputs in feature screens.',
    usageStatus: 'High'
  },
  {
    id: 'ui-select',
    name: 'Select',
    selector: '<ui-select>',
    category: 'Form Controls',
    functionality: 'Standardized select/dropdown wrapper for controlled choice fields.',
    designNeeds:
      'Consistent sizing with text fields, clear selected state and keyboard-accessible options.',
    interactionNotes: 'Emits selected value and supports mapped option lists.',
    usageGuidelines: 'Use for all enumerated form choices and filters.',
    usageStatus: 'High'
  },
  {
    id: 'ui-toggle',
    name: 'Toggle',
    selector: '<ui-toggle>',
    category: 'Form Controls',
    functionality: 'Compact on/off switch wrapper for approval, availability and binary state interactions.',
    designNeeds:
      'Dense admin scale, clear checked contrast, and predictable disabled treatment within tables and forms.',
    interactionNotes: 'Emits boolean toggle changes and works well inside shared table cells and approval flows.',
    usageGuidelines: 'Use for binary actions when a switch is more readable than a checkbox.',
    usageStatus: 'Medium'
  },
  {
    id: 'ui-button',
    name: 'Button',
    selector: '<ui-button>',
    category: 'Form Controls',
    functionality: 'Unified action button with primary, secondary, danger and ghost variants.',
    designNeeds:
      'Brand-compliant variant colors, spacing scale alignment and robust disabled-state contrast.',
    interactionNotes: 'Supports size options, type handling and accessibility labeling.',
    usageGuidelines: 'Use for all clickable actions, never raw button tags in features.',
    usageStatus: 'High'
  },
  {
    id: 'ui-badge',
    name: 'Badge',
    selector: '<ui-badge>',
    category: 'Feedback',
    functionality: 'Displays compact status and semantic labels using predefined tones.',
    designNeeds:
      'Sharp radius, high-contrast text and semantically stable color mapping across screens.',
    interactionNotes: 'Supports readable state labeling inside cards and tables.',
    usageGuidelines: 'Use for statuses like active, pending, overdue and plan/state tags.',
    usageStatus: 'High'
  },
  {
    id: 'ui-modal',
    name: 'Modal',
    selector: '<ui-modal>',
    category: 'Feedback',
    functionality: 'Reusable dialog shell for confirmations and form flows.',
    designNeeds:
      'Layered elevation, focused content width, safe close affordance and clear action ordering.',
    interactionNotes: 'Supports projected content and action sections for confirmations.',
    usageGuidelines: 'Use for create/edit confirmations and short data entry dialogs.',
    usageStatus: 'Medium'
  },
  {
    id: 'ui-dialog',
    name: 'Dialog',
    selector: '<ui-dialog>',
    category: 'Feedback',
    functionality: 'Shared confirmation dialog wrapper with configurable title, tone, and primary/secondary actions.',
    designNeeds:
      'Centered overlay, clear destructive styling for danger actions, and accessible focus-safe close paths.',
    interactionNotes: 'Supports confirm/cancel outputs and backdrop cancel for quick decision workflows.',
    usageGuidelines: 'Use for destructive or high-impact confirmations such as delete, archive, and reset actions.',
    usageStatus: 'High'
  },
  {
    id: 'ui-toast',
    name: 'Toast',
    selector: '<ui-toast>',
    category: 'Feedback',
    functionality: 'Transient notification component for success, warning and failure feedback.',
    designNeeds:
      'Clear semantic colors, concise copy and unobtrusive placement over content.',
    interactionNotes: 'Designed for quick, non-blocking system feedback.',
    usageGuidelines: 'Use for post-action acknowledgements and validation feedback summaries.',
    usageStatus: 'Medium'
  }
];
